import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { getCohortBuilderPath, getProjectCohortVersionPath } from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import {
  useCreateCohortMutation,
  useCreateCohortProfileMutation,
  useSaveCohortVersionMutation,
} from '@verantos-dev/data-access-cohorts';
import { type CohortVersion } from '@verantos-dev/types-cohorts';
import { type ObjectResponse } from '@verantos-dev/types-common';
import { type ProjectDetail } from '@verantos-dev/types-projects';

export const useCohortCreation = (
  projectId: string | undefined,
  projectData: ObjectResponse<ProjectDetail> | undefined,
  sourceCohortVersion: ObjectResponse<CohortVersion> | undefined,
  newCohortName: string,
  itemName: string | null
) => {
  const navigate = useNavigate();
  const intl = useIntl();
  // Gen-3 routes create through the keyed TanStack mutation so the project-page
  // artifact manifest + activity feed refresh when a cohort is copied in
  // (OR-6111); legacy keeps the RTK mutation untouched. Both hooks are called
  // unconditionally (rules of hooks); only the selected one is invoked.
  // TODO: cleanup with research_assistant_gen_3 ff — drop the RTK branch.
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;
  const [rtkCreateCohort, { isLoading: rtkCreatingCohort }] = useCreateCohortMutation();
  const { mutateAsync: gen3CreateCohort, isPending: gen3CreatingCohort } =
    useCreateCohortProfileMutation();
  const isCreatingCohort = isGen3Enabled ? gen3CreatingCohort : rtkCreatingCohort;
  const { mutateAsync: saveCohortVersion, isPending: isCreatingVersion } =
    useSaveCohortVersionMutation();

  const handleSubmit = useCallback(async () => {
    try {
      // Create new cohort
      const createArgs = {
        projectId: projectId || '',
        data: {
          cohortName: newCohortName,
          cohortDescription: `Copy of ${itemName}`,
          datasetReleaseId: projectData?.data?.datasetReleaseId || '',
        },
      };
      const newCohort = isGen3Enabled
        ? await gen3CreateCohort(createArgs)
        : await rtkCreateCohort(createArgs).unwrap();

      // Copy cohort version details
      const newVersion = await saveCohortVersion({
        projectId: projectId || '',
        profileId: newCohort.data.cohortProfileId,
        dslVersion: sourceCohortVersion?.data?.dslVersion || '',
        sqlValue: sourceCohortVersion?.data?.sqlValue || '',
        jsonValue: sourceCohortVersion?.data?.jsonValue,
      });

      // Navigate to the copied cohort. Gen-3 lands on the new agent-first
      // cohort builder (definition tab); legacy keeps the old builder route.
      // Mirrors useCreateCohortModal's redirect.
      // TODO: cleanup with research_assistant_gen_3 ff — drop the legacy branch.
      const destinationBase = isGen3Enabled
        ? getProjectCohortVersionPath(
            projectId || '',
            newCohort.data.cohortProfileId,
            newVersion.data.cohortVersionId
          )
        : getCohortBuilderPath(
            projectId || '',
            newCohort.data.cohortProfileId,
            newVersion.data.cohortVersionId
          );
      const destinationTab = isGen3Enabled
        ? CohortBuilderPageTab.definition
        : CohortBuilderPageTab.events;
      navigate(`${destinationBase}?pageTab=${destinationTab}`);

      return {
        success: true,
        successMessage: intl.formatMessage(
          { id: 'cohortsList.copyCohortModal.success.text' },
          { cohortName: newCohort.data.cohortName }
        ),
      };
    } catch (error) {
      console.error('Error copying cohort:', error);
      throw error;
    }
  }, [
    projectId,
    newCohortName,
    itemName,
    isGen3Enabled,
    gen3CreateCohort,
    rtkCreateCohort,
    saveCohortVersion,
    navigate,
    intl,
    projectData,
    sourceCohortVersion,
  ]);

  return {
    handleSubmit,
    isCreatingCohort,
    isCreatingVersion,
  };
};
